import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { API_PATHS } from '../config/api-paths';
import { LenderPortfolioApi } from '../tokens/lender-portfolio.token';
import { LenderPortfolioPage, LenderPortfolioQuery, LenderPortfolioRow } from '../models/lender-origination.model';

/**
 * REST-only implementation of {@link LenderPortfolioApi} (EM-699 / F3 — EM-708).
 *
 * Calls the B6 `LenderServicingResource`:
 *   GET /services/embedded/api/v1/lender/portfolio?ed=&risk=&status=&q=&page=&size=
 *
 * The rows arrive in the body; the total element count arrives in the JHipster `X-Total-Count`
 * header (PaginationUtil) — so the service reads the full `HttpResponse` to surface server-side
 * pagination. Rule 07 / Rule 10 — no Mock. Rule 09 — the raw error propagates.
 */
@Injectable()
export class LenderPortfolioRestService implements LenderPortfolioApi {
  private readonly http = inject(HttpClient);
  private readonly base = API_PATHS.LENDER_SERVICING_PORTFOLIO;

  getPortfolio(query: LenderPortfolioQuery): Observable<LenderPortfolioPage> {
    const page = query.page ?? 0;
    const size = query.size ?? 20;
    let params = new HttpParams().set('page', String(page)).set('size', String(size));
    if (query.ed != null) {
      params = params.set('ed', String(query.ed));
    }
    if (query.risk) {
      params = params.set('risk', query.risk);
    }
    if (query.status) {
      params = params.set('status', query.status);
    }
    if (query.q) {
      params = params.set('q', query.q);
    }
    return this.http
      .get<LenderPortfolioRow[]>(this.base, { params, observe: 'response' })
      .pipe(map((resp: HttpResponse<LenderPortfolioRow[]>) => this.toPage(resp, page, size)));
  }

  private toPage(resp: HttpResponse<LenderPortfolioRow[]>, page: number, size: number): LenderPortfolioPage {
    const totalHeader = resp.headers.get('X-Total-Count');
    const totalCount = totalHeader != null ? Number(totalHeader) : null;
    return {
      rows: resp.body ?? [],
      page,
      size,
      totalCount: Number.isFinite(totalCount as number) ? totalCount : null,
    };
  }
}
